/**
 * DriveFilesPickerModal — Klaris-owned modal for managing Google Drive
 * file selection on a Drive connector.
 *
 * Why this exists:
 *   Google's native Picker is great for *discovery* (it gives users their
 *   full Drive tree, search, recents, shared drives, etc.) but it's a
 *   write-only "add files" UI: it can't show what you've already selected
 *   and it can't deselect saved files. That used to mean users had to
 *   navigate to a different page just to remove a file, which is bad.
 *
 *   This modal owns the canonical selection state. It still launches the
 *   native Picker as an "Add files" sub-flow — so users keep Drive's
 *   familiar discovery UX — but every file Klaris knows about lives in
 *   this list, with a clear remove button and (for Sheets/Excel) the
 *   ability to drill in and pick specific tabs.
 *
 * Sheet selection:
 *   `selected_drive_files` items can carry `selected_sheets?: string[]`.
 *   Missing/null = "all sheets" (preserves backwards-compatible behaviour
 *   for existing connectors). An explicit array narrows the import to
 *   just those tabs. CSVs ignore the field entirely.
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Checkbox,
  Collapse,
  Chip,
  LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { AppDialog } from './dialog';
import { showGoogleDrivePicker, GOOGLE_DRIVE_MIME_TYPES } from '../utils/googlePicker';
import { buildApiUrl, config as appConfig } from '../config';
import { SnackbarContext } from '../ui/SnackbarProvider';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DriveFile {
  id: string;
  name: string;
  mimeType?: string;
  /**
   * If present, only these sheets are processed during sync. Omitted/null
   * means "all sheets" (the default for new selections, and what existing
   * connectors implicitly used before sheet selection existed).
   */
  selected_sheets?: string[] | null;
}

interface DriveFilesPickerModalProps {
  open: boolean;
  connectorId: string;
  connectorName?: string | null;
  tenantId: string;
  /** Initial selection from `connector_metadata.selected_drive_files`. */
  initialFiles: DriveFile[];
  authHeader: () => Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}

interface SheetCacheEntry {
  loading: boolean;
  sheets: string[] | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SPREADSHEET_MIMES = new Set([
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

function isSpreadsheet(mimeType?: string) {
  if (!mimeType) return false;
  if (SPREADSHEET_MIMES.has(mimeType)) return true;
  return /spreadsheet|excel/i.test(mimeType);
}

function fileIcon(mimeType?: string) {
  if (isSpreadsheet(mimeType)) return GridOnRounded;
  if (mimeType?.includes('csv')) return ArticleRounded;
  return InsertDriveFileRounded;
}

function fileTypeLabel(mimeType?: string): string {
  if (!mimeType) return 'File';
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return 'Google Sheet';
  if (mimeType.includes('spreadsheetml')) return 'Excel';
  if (mimeType.includes('ms-excel')) return 'Excel';
  if (mimeType.includes('csv')) return 'CSV';
  return 'File';
}

function sheetsSummary(file: DriveFile, cached?: SheetCacheEntry): string | null {
  if (!isSpreadsheet(file.mimeType)) return null;
  const total = cached?.sheets?.length;
  if (file.selected_sheets == null) {
    return total != null ? `All sheets (${total})` : 'All sheets';
  }
  if (file.selected_sheets.length === 0) return 'No sheets selected';
  if (total != null && file.selected_sheets.length === total) {
    return `All sheets (${total})`;
  }
  return total != null
    ? `${file.selected_sheets.length} of ${total} sheets`
    : `${file.selected_sheets.length} sheets`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DriveFilesPickerModal: React.FC<DriveFilesPickerModalProps> = ({
  open,
  connectorId,
  connectorName,
  tenantId,
  initialFiles,
  authHeader,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const { notify } = React.useContext(SnackbarContext);

  const [files, setFiles] = React.useState<DriveFile[]>([]);
  const [search, setSearch] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pickerLoading, setPickerLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [sheetCache, setSheetCache] = React.useState<Record<string, SheetCacheEntry>>({});

  // Reset on (re)open so the user is always editing fresh state.
  React.useEffect(() => {
    if (!open) return;
    setFiles(initialFiles.map(f => ({ ...f })));
    setSearch('');
    setError(null);
    setExpanded(new Set());
    setSheetCache({});
  }, [open, initialFiles]);

  // ------- sheet listing (lazy, per-file) -----------------------------------
  const loadSheetsForFile = React.useCallback(async (fileId: string) => {
    setSheetCache(prev => {
      if (prev[fileId]?.sheets || prev[fileId]?.loading) return prev;
      return { ...prev, [fileId]: { loading: true, sheets: null, error: null } };
    });
    try {
      const r = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/drive-files/${encodeURIComponent(fileId)}/sheets`),
        { headers: authHeader() as HeadersInit },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any).detail || `Failed to load sheets (${r.status})`);
      }
      const data = await r.json();
      const sheetNames: string[] = (data.sheets || []).map((s: any) => s.name);
      setSheetCache(prev => ({
        ...prev,
        [fileId]: { loading: false, sheets: sheetNames, error: null },
      }));
    } catch (e: any) {
      setSheetCache(prev => ({
        ...prev,
        [fileId]: { loading: false, sheets: null, error: e?.message || 'Failed to load sheets' },
      }));
    }
  }, [tenantId, connectorId, authHeader]);

  const toggleExpanded = React.useCallback((fileId: string, mimeType?: string) => {
    if (!isSpreadsheet(mimeType)) return;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
        // Lazy-load sheets the first time we expand
        if (!sheetCache[fileId]?.sheets && !sheetCache[fileId]?.loading) {
          loadSheetsForFile(fileId);
        }
      }
      return next;
    });
  }, [sheetCache, loadSheetsForFile]);

  // ------- file mutations ---------------------------------------------------
  const removeFile = React.useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const setFileSheets = React.useCallback((fileId: string, sheets: string[] | null) => {
    setFiles(prev => prev.map(f => (f.id === fileId ? { ...f, selected_sheets: sheets } : f)));
  }, []);

  const toggleSheet = React.useCallback((fileId: string, sheetName: string, allSheets: string[]) => {
    setFiles(prev => prev.map(f => {
      if (f.id !== fileId) return f;
      // If currently "all sheets" (null), materialize the full list first
      // so toggling off feels predictable.
      const current = f.selected_sheets == null ? [...allSheets] : [...f.selected_sheets];
      const idx = current.indexOf(sheetName);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(sheetName);
      // If user re-selected everything, collapse back to "all sheets" (null)
      // — keeps the default behaviour stable when the workbook later grows.
      if (current.length === allSheets.length) {
        return { ...f, selected_sheets: null };
      }
      return { ...f, selected_sheets: current };
    }));
  }, []);

  // ------- add files via Google's native picker -----------------------------
  const handleAddFiles = React.useCallback(async () => {
    const developerKey = appConfig.googleApiKey;
    if (!developerKey) {
      setError('Google API key is not configured. Cannot open file browser.');
      return;
    }
    setPickerLoading(true);
    setError(null);
    try {
      const tokenRes = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/access-token`),
        { headers: authHeader() as HeadersInit },
      );
      if (!tokenRes.ok) throw new Error('Failed to fetch Google access token');
      const { access_token: accessToken } = await tokenRes.json();
      if (!accessToken) throw new Error('No Google access token returned');

      await showGoogleDrivePicker(
        accessToken,
        developerKey,
        (docs) => {
          // Merge: keep existing entries (so per-file sheet selections aren't
          // lost when re-opening the picker) and append any newly chosen files.
          setFiles(prev => {
            const byId = new Map<string, DriveFile>();
            prev.forEach(f => byId.set(f.id, f));
            docs.forEach(d => {
              if (!byId.has(d.id)) {
                byId.set(d.id, { id: d.id, name: d.name, mimeType: d.mimeType });
              }
            });
            return Array.from(byId.values());
          });
        },
        () => { /* picker cancelled */ },
        {
          multiselect: true,
          title: 'Add files from Google Drive',
          mimeTypes: GOOGLE_DRIVE_MIME_TYPES.ALL_DOCUMENTS,
        },
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to open Google Drive picker');
    } finally {
      setPickerLoading(false);
    }
  }, [tenantId, connectorId, authHeader]);

  // ------- save -------------------------------------------------------------
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = files.map(f => {
        const out: DriveFile = { id: f.id, name: f.name, mimeType: f.mimeType };
        if (f.selected_sheets != null) out.selected_sheets = f.selected_sheets;
        return out;
      });
      const r = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({
            connector_metadata: {
              selected_drive_files: payload,
              // Mirror to selected_table_names so callers that key off either
              // field keep working — kept consistent with the existing flow.
              selected_table_names: payload.map(f => f.id),
            },
          }),
        },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any).detail || 'Failed to save selection');
      }
      notify(`${payload.length} file${payload.length !== 1 ? 's' : ''} saved`, 'success');
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save selection');
    } finally {
      setSaving(false);
    }
  };

  // ------- derived ----------------------------------------------------------
  const filtered = files.filter(f =>
    !search || f.name.toLowerCase().includes(search.toLowerCase()),
  );
  const sheetFilteredCount = files.reduce(
    (n, f) => n + (Array.isArray(f.selected_sheets) ? 1 : 0),
    0,
  );

  const subtitleParts = [
    connectorName || 'Google Drive',
    `${files.length} file${files.length !== 1 ? 's' : ''} selected`,
  ];
  if (sheetFilteredCount > 0) {
    subtitleParts.push(`${sheetFilteredCount} with sheet filters`);
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title="Select Google Drive files"
      subtitle={subtitleParts.join(' · ')}
      busy={saving}
      contentSx={{ px: 0, py: 0 }}
      primaryAction={{
        label: saving ? 'Saving…' : `Save${files.length > 0 ? ` (${files.length})` : ''}`,
        onClick: handleSave,
        disabled: saving,
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
      actionsLeftExtra={
        <Typography variant="caption" color="text.secondary">
          {files.length === 0
            ? 'No files selected'
            : `${files.length} file${files.length !== 1 ? 's' : ''} ready to sync`}
        </Typography>
      }
    >
      {/* Toolbar */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            placeholder="Search selected files…"
            size="small"
            fullWidth
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchRounded sx={{ fontSize: 16, color: 'text.disabled', mr: 1 }} />,
            }}
            sx={{
              flex: { sm: '1 1 0%' },
              minWidth: 0,
              '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8125rem', height: 36 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={pickerLoading ? <CircularProgress size={14} color="inherit" /> : <AddRounded sx={{ fontSize: 18 }} />}
            disabled={pickerLoading}
            onClick={handleAddFiles}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              height: 36,
              px: 1.75,
            }}
          >
            {pickerLoading ? 'Opening…' : 'Add files from Drive'}
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1.25, borderRadius: '8px', fontSize: '0.8125rem' }}>{error}</Alert>
        )}
      </Box>

      {/* Body */}
      <Box sx={(t) => ({ px: 0, pb: 1.5, flex: 1, overflow: 'auto', borderTop: `1px solid ${t.tokens.border.subtle}` })}>
        {files.length === 0 ? (
          <EmptyState onAdd={handleAddFiles} loading={pickerLoading} />
        ) : filtered.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
            <SearchRounded sx={{ fontSize: 32, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">No files match "{search}"</Typography>
          </Box>
        ) : (
          <Box sx={{ py: 0.5 }}>
            {filtered.map((f, idx) => (
              <FileRow
                key={f.id}
                file={f}
                first={idx === 0}
                expanded={expanded.has(f.id)}
                sheetCache={sheetCache[f.id]}
                onToggleExpand={() => toggleExpanded(f.id, f.mimeType)}
                onRemove={() => removeFile(f.id)}
                onToggleSheet={(name, all) => toggleSheet(f.id, name, all)}
                onSelectAllSheets={() => setFileSheets(f.id, null)}
                onClearSheets={() => setFileSheets(f.id, [])}
              />
            ))}
          </Box>
        )}
      </Box>
    </AppDialog>
  );
};

export default DriveFilesPickerModal;

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

const EmptyState: React.FC<{ onAdd: () => void; loading: boolean }> = ({ onAdd, loading }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 7, px: 3, gap: 1.5, textAlign: 'center' }}>
    <Box sx={(t) => ({
      width: 56, height: 56, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: t.tokens.brand.primarySoft, color: 'primary.main',
    })}>
      <InsertDriveFileRounded sx={{ fontSize: 28 }} />
    </Box>
    <Typography variant="subtitle2" fontWeight={600}>No files selected yet</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>
      Pick the Google Sheets, Excel, or CSV files you want Klaris to sync. You can choose specific tabs after adding them.
    </Typography>
    <Button
      variant="contained"
      size="small"
      disableElevation
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AddRounded sx={{ fontSize: 18 }} />}
      disabled={loading}
      onClick={onAdd}
      sx={{ borderRadius: '8px', textTransform: 'none', mt: 0.5, fontWeight: 600, px: 2 }}
    >
      {loading ? 'Opening…' : 'Add files from Drive'}
    </Button>
  </Box>
);

// ---------------------------------------------------------------------------
// File row (with collapsible sheet picker)
// ---------------------------------------------------------------------------

interface FileRowProps {
  file: DriveFile;
  first: boolean;
  expanded: boolean;
  sheetCache?: SheetCacheEntry;
  onToggleExpand: () => void;
  onRemove: () => void;
  onToggleSheet: (name: string, allSheets: string[]) => void;
  onSelectAllSheets: () => void;
  onClearSheets: () => void;
}

const FileRow: React.FC<FileRowProps> = ({
  file,
  first,
  expanded,
  sheetCache,
  onToggleExpand,
  onRemove,
  onToggleSheet,
  onSelectAllSheets,
  onClearSheets,
}) => {
  const Icon = fileIcon(file.mimeType);
  const expandable = isSpreadsheet(file.mimeType);
  const summary = sheetsSummary(file, sheetCache);

  return (
    <Box
      sx={(t) => ({
        borderTop: first ? 'none' : `1px solid ${t.tokens.border.subtle}`,
      })}
    >
      <Box
        onClick={expandable ? onToggleExpand : undefined}
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          py: 1.25,
          cursor: expandable ? 'pointer' : 'default',
          transition: 'background-color 120ms ease',
          '&:hover': expandable ? { bgcolor: t.tokens.surface.sunken } : undefined,
        })}
      >
        {expandable ? (
          <Box sx={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
            {expanded
              ? <ExpandMoreRounded sx={{ fontSize: 18 }} />
              : <ChevronRightRounded sx={{ fontSize: 18 }} />}
          </Box>
        ) : (
          <Box sx={{ width: 18 }} />
        )}
        <Box sx={(t) => ({
          width: 32, height: 32, borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.surface.sunken,
          color: file.mimeType === 'application/vnd.google-apps.spreadsheet'
            ? '#0F9D58' // Google Sheets green
            : (file.mimeType?.includes('csv') ? '#34A853' : 'text.secondary'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        })}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={500} noWrap title={file.name}>{file.name}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {fileTypeLabel(file.mimeType)}
            </Typography>
            {summary && (
              <>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>·</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{summary}</Typography>
              </>
            )}
          </Stack>
        </Box>
        {Array.isArray(file.selected_sheets) && file.selected_sheets.length > 0 && (
          <Chip
            size="small"
            label="Filtered"
            sx={(t) => ({
              height: 20,
              fontSize: '0.6875rem',
              borderRadius: `${t.ds.radius.sm}px`,
              bgcolor: t.tokens.brand.primarySoft,
              color: 'primary.main',
              fontWeight: 600,
            })}
          />
        )}
        <Tooltip title="Remove file from selection">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            sx={(t) => ({
              color: 'text.disabled',
              '&:hover': { color: 'error.main', bgcolor: t.tokens.surface.sunken },
            })}
          >
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {expandable && (
        <Collapse in={expanded} unmountOnExit>
          <SheetPicker
            file={file}
            cache={sheetCache}
            onToggleSheet={onToggleSheet}
            onSelectAllSheets={onSelectAllSheets}
            onClearSheets={onClearSheets}
          />
        </Collapse>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sheet picker (inline, per file)
// ---------------------------------------------------------------------------

interface SheetPickerProps {
  file: DriveFile;
  cache?: SheetCacheEntry;
  onToggleSheet: (name: string, allSheets: string[]) => void;
  onSelectAllSheets: () => void;
  onClearSheets: () => void;
}

const SheetPicker: React.FC<SheetPickerProps> = ({
  file,
  cache,
  onToggleSheet,
  onSelectAllSheets,
  onClearSheets,
}) => {
  if (cache?.loading || !cache) {
    return (
      <Box sx={{ pl: { xs: 5.5, sm: 6.5 }, pr: { xs: 2, sm: 3 }, pb: 1.5 }}>
        <LinearProgress sx={{ borderRadius: 1, height: 2 }} />
      </Box>
    );
  }
  if (cache.error) {
    return (
      <Box sx={{ pl: { xs: 5.5, sm: 6.5 }, pr: { xs: 2, sm: 3 }, pb: 1.5 }}>
        <Alert severity="warning" sx={{ borderRadius: '8px', fontSize: '0.8125rem' }}>
          {cache.error}
        </Alert>
      </Box>
    );
  }
  const sheets = cache.sheets || [];
  if (sheets.length === 0) {
    return (
      <Box sx={(t) => ({
        pl: { xs: 5.5, sm: 6.5 }, pr: { xs: 2, sm: 3 }, pb: 1.5,
        display: 'flex', alignItems: 'center', gap: 0.75,
      })}>
        <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.secondary">No tabs found in this file.</Typography>
      </Box>
    );
  }
  const isAll = file.selected_sheets == null;
  const selectedSet = new Set(isAll ? sheets : (file.selected_sheets || []));
  const allSelected = selectedSet.size === sheets.length;
  const noneSelected = selectedSet.size === 0;

  return (
    <Box sx={(t) => ({
      pl: { xs: 5.5, sm: 6.5 },
      pr: { xs: 2, sm: 3 },
      pb: 1.5,
      bgcolor: t.tokens.surface.sunken,
    })}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.75 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
          Tabs · {selectedSet.size} of {sheets.length} selected
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            variant="text"
            disabled={allSelected && isAll}
            onClick={onSelectAllSheets}
            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 1, py: 0.25, fontWeight: 600 }}
          >
            All sheets
          </Button>
          <Button
            size="small"
            variant="text"
            color="inherit"
            disabled={noneSelected}
            onClick={onClearSheets}
            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 1, py: 0.25, color: 'text.secondary' }}
          >
            None
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.25 }}>
        {sheets.map(name => {
          const checked = selectedSet.has(name);
          return (
            <Box
              key={name}
              onClick={() => onToggleSheet(name, sheets)}
              sx={(t) => ({
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1, py: 0.5, borderRadius: `${t.ds.radius.sm}px`, cursor: 'pointer',
                '&:hover': { bgcolor: t.tokens.surface.raised },
              })}
            >
              <Checkbox
                checked={checked}
                size="small"
                disableRipple
                sx={{ p: 0 }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                onChange={() => onToggleSheet(name, sheets)}
              />
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: checked ? 500 : 400 }} noWrap>
                {name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
